import { useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { GenericError } from '@/components/GenericError/GenericError';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useUser } from '@/models/user';
import { ErrorState } from '@/routes/SettingsBeta/components/ErrorState';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';
import { UpdateUserRoles } from '@/routes/SettingsBeta/SettingsOrganization/sections/Members/subsections/Invitations/components/UpdateUserRoles';
import { NotFoundError } from '@/utils/error/NotFoundError';

import { groupMemberships } from './group-memberships';

export const User = () => {
  const { id } = useParams();
  const { t } = useLingui();

  const { data: user, isLoading, isError, error } = useUser(id);

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !user) {
    let errorMessage = <GenericError />;

    if (error instanceof NotFoundError) {
      errorMessage = (
        <Paragraph>
          <Trans id="settings.organization.members.users.detail.not-found">
            Unknown user or you do not have access to them.
          </Trans>
        </Paragraph>
      );
    }

    return <ErrorState errorMessage={errorMessage} />;
  }

  const memberships = groupMemberships(user.roles);

  return (
    <main>
      <Stack gap="1.5rem">
        <Stack
          row
          className="settings-section__record-header"
          gap="0.5rem"
          alignItems="center"
          flexWrap="wrap"
        >
          <SettingsBreadcrumbParent
            to="/settings_beta/team/users"
            label={<Trans id="settings.organization.members.users">Users</Trans>}
          />
          <Title as="h1" size="200" weight="medium">
            {user.name}
          </Title>
        </Stack>

        <Stack gap="1rem">
          <Title as="h3" size="300" weight="medium">
            <Trans id="settings.organization.members.users.detail.informations">Informations</Trans>
          </Title>

          <Box>
            <Stack row>
              <InlineText weight="medium" style={{ width: '20%' }}>
                <Trans id="settings.organization.members.users.detail.email">Email</Trans>
              </InlineText>
              <InlineText style={{ width: '80%' }}>{user.email}</InlineText>
            </Stack>
          </Box>

          <Stack row gap="1rem" justifyContent="space-between" alignItems="center">
            <Title as="h3" size="300" weight="medium">
              <Trans id="settings.organization.members.users.detail.memberships.title">
                Memberships
              </Trans>
            </Title>
            <UpdateUserRoles
              buttonProps={{ size: 'medium' }}
              user={{
                id: user.id,
                name: user.name,
                email: user.email,
                roles: user.roles,
              }}
            />
          </Stack>

          <Table
            columnWidths={['1fr', '1fr']}
            bordered
            ariaLabel={t({
              id: 'settings.organization.members.users.detail.memberships.table.aria_label',
              message: 'User memberships',
            })}
          >
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.organization.members.users.detail.memberships.table.header.roles">
                    Roles
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.members.users.detail.memberships.table.header.entity">
                    Entity
                  </Trans>
                </Column>
              </Row>
            </Header>
            <Body>
              {memberships.length === 0 ? (
                <Row>
                  <Cell isWholeRow>
                    <Paragraph>
                      <Trans id="settings.organization.members.users.detail.memberships.empty">
                        This user has no memberships yet.
                      </Trans>
                    </Paragraph>
                  </Cell>
                </Row>
              ) : (
                memberships.map((membership) => (
                  <Row key={membership.entity?.id ?? 'organization'}>
                    <Cell>
                      <Stack gap="0.25rem">
                        {membership.roles.map((role) => (
                          <Button
                            variant="link-subtle"
                            key={role.id}
                            to={`/settings_beta/team/roles/${role.id}`}
                          >
                            <Paragraph size="100" weight="medium">
                              {role.name}
                            </Paragraph>
                          </Button>
                        ))}
                      </Stack>
                    </Cell>
                    <Cell>
                      {membership.entity ? (
                        <Button
                          variant="link-subtle"
                          to={`/settings_beta/network/entities/${membership.entity.id}`}
                        >
                          <Paragraph>{membership.entity.name}</Paragraph>
                        </Button>
                      ) : (
                        '-'
                      )}
                    </Cell>
                  </Row>
                ))
              )}
            </Body>
          </Table>
        </Stack>
      </Stack>
    </main>
  );
};
